<template>
    <hts-drawer
        v-model="visible"
        color="main"
        :rules="rulesAddWorkflow"
        :submit="handleSubmit"
        :title="$t('labels.workflowModule.add')"
        size="large"
        @close="close">
        <div :style="{ minWidth: minWidthScroll }">
            <hts-card :title="$t('labels.generalInformation')">
                <hts-form-input
                    v-model="dataAdd.name"
                    :label="$t('labels.workflowModule.name')"
                    :required="true"
                    name="workflowModule.name"
                    prop="name" />
                <hts-form-select
                    v-model="dataAdd.transportation"
                    :items="transportation"
                    :label="$t('labels.transportation')"
                    required
                    name="transportation"
                    prop="transportation" />

                <hts-form-checkbox
                    v-model="dataAdd.requireContainerNumber"
                    :checked="!!dataAdd.requireContainerNumber"
                    :items="[{ label: 'Yêu cầu số container trước khi điều phối', value: true }]" />
            </hts-card>

            <hts-card
                :title="$t('labels.workflow')"
                class="relative">
                <hts-form-button
                    class="mb-4"
                    color="info"
                    :outline="false"
                    icon-name="hi-cog-8-tooth-icon"
                    @click="openSettingPositionNumber">
                    {{ $t('labels.settingPositionNumber') }}
                </hts-form-button>

                <setting-position-number
                    ref="settingPositionNumber"
                    @confirm="(val: number) => dataAdd.positionNumber = val" />

                <hts-workflow-position
                    :positions="dataAdd.positions"
                    :is-active="(item: any) => item.tasks?.length">
                    <template #default="{ item, index }">
                        <div class="w-72 mb-2 px-2 box-border">
                            <hts-admincp-select-node-type
                                v-model="item.nodeType"
                                v-model:text="item.nodeTypeName"
                                required
                                key-id="_id"
                                key-label="name"
                                :filterable="true"
                                :clearable="true"
                                :prop="'nodeType' + index"
                                :name="'nodeType' + index"
                                confirm-change
                                :title-confirm="$t('labels.changeNodeType')"
                                :detail-confirm="$t('labels.changeNodeTypeInfo')"
                                @change="() => clearBusinessAndTask(index)" />
                        </div>
                        <div class="flex justify-center">
                            <hi-pencil-square-icon
                                class="h-7 w-7 text-info-500 cursor-pointer"
                                @click="openWorkflowPosition(item, index === 0 || index === dataAdd.positions.length - 1, index === dataAdd.positions.length - 1)" />
                        </div>
                    </template>
                </hts-workflow-position>

                <p class="mb-4">
                    {{ $t('labels.theSamePositions') }}
                </p>

                <div
                    v-if="dataAdd.theSamePositions"
                    class="mb-3 text-center">
                    <div
                        v-for="(theSamePosition, index) in dataAdd.theSamePositions"
                        :key="index"
                        class="grid-cols-6 gap-4 grid min-w-full"
                        :class="{ 'mt-4': index !== 0 }">
                        <hts-form-select
                            v-model="dataAdd.theSamePositions[index][0]"
                            class="col-span-2"
                            :items="dataSelectTheSameNodes"
                            key-id="key"
                            key-label="value"
                            :color="color"
                            required />

                        <p class="text-3xl text-center mt-3">
                            =
                        </p>

                        <hts-form-select
                            v-model="dataAdd.theSamePositions[index][1]"
                            class="col-span-2"
                            :items="dataSelectTheSameNodes"
                            key-id="key"
                            key-label="value"
                            :color="color"
                            required />

                        <hi-x-circle-icon
                            class="mt-2 w-7 h-7 text-error-500 hover:text-error-600 cursor-pointer"
                            @click="deleteTheSamePosition(index)" />
                    </div>
                </div>

                <div class="mt-3 text-center">
                    <hts-form-button
                        color="main"
                        icon-name="hi-plus-circle-icon"
                        icon-position="right"
                        type="button"
                        size="small"
                        @click="addTheSamePosition">
                        {{ $t('labels.add2') }}
                    </hts-form-button>
                </div>
            </hts-card>
        </div>

        <workflow-position
            ref="workflowPosition"
            @update="(val) => updateWorkflowPosition(val)" />
    </hts-drawer>
</template>
<script setup lang="ts">
import { rulesAddWorkflow, IAddWorkflow, IWorkflowPosition } from '../-imports/form'
import data from '../-imports/data'
import SettingPositionNumber from './setting-position-number.vue'
import WorkflowPosition from './workflow-position.vue'
import { $urlAdmin } from '#imports'

import screen from '~/components/utils/screen'

const { transportation } = data()
const { $disableReactive } = useNuxtApp()
const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  }
})

const { color } = toRefs(props)

const visible = ref(false)

const emits = defineEmits(['close', 'refreshTables', 'newTask', 'update:show'])

const close = () => {
  visible.value = false
}

const open = () => {
  visible.value = true
}

const dataAdd = reactive<IAddWorkflow>({
  name: '',
  transportation: '',
  positionNumber: null,
  requireContainerNumber: false,
  positions: [],
  theSamePositions: []
})

const settingPositionNumber = ref()
const workflowPosition = ref()
const openSettingPositionNumber = () => {
  if (settingPositionNumber.value) {
    settingPositionNumber.value.open(dataAdd.positionNumber)
  }
}

const resetFields = () => {
  dataAdd.name = ''
  dataAdd.transportation = ''
  dataAdd.requireContainerNumber = false
  dataAdd.positionNumber = null
  dataAdd.positions = []
  dataAdd.theSamePositions = []
}

watch(
  () => dataAdd.positionNumber,
  (val: number | null) => {
    if (!val || typeof val !== 'number' || val < 0) {
      dataAdd.positions = [
        {
          index: 0,
          nodeType: '',
          nodeTypeName: '',
          businesses: {},
          tasks: []
        }
      ]
      return
    }

    const positions: IWorkflowPosition[] = []

    for (let i = 0; i < val; i++) {
      positions.push({
        index: i + 1,
        nodeType: '',
        nodeTypeName: '',
        businesses: {},
        tasks: []
      })
    }

    dataAdd.positions = positions
  }
)

const openWorkflowPosition = (position: IWorkflowPosition, checkActiveVehicle: boolean, checkHaveTaskLatest: boolean) => {
  if (workflowPosition.value) {
    workflowPosition.value.open($disableReactive(position), dataAdd.transportation, checkActiveVehicle, checkHaveTaskLatest)
  }
}

const updateWorkflowPosition = (position: IWorkflowPosition) => {
  const index = position.index - 1
  dataAdd.positions[index] = position
}

const handleSubmit = () => {
  const api = $urlAdmin.workflow.add

  const data: any = { ...dataAdd }
  if (data.transportation !== 'CONTAINER') {
    data.requireContainerNumber = false
  }

  $api(api, { body: data }).then((data) => {
    const resData: any = data?.data?.value

    if (resData.success) {
      emits('refreshTables')
      emits('newTask', resData.data)
      process.client && $toast().success($t('labels.addSuccess'))
      close()
      resetFields()
    }
  })
}

const columnClass = computed(() => {
  if ($lodash.inRange(props.column, 1, 12)) {
    return `grid-cols-${props.column}`
  }

  return 'grid-cols-1'
})

const dataSelectTheSameNodes = computed(() => {
  if (!dataAdd.positions || !Array.isArray(dataAdd.positions)) {
    return []
  }

  return dataAdd.positions.map((item: any, index: number) => {
    return {
      key: index,
      value: `${$t('labels.position')} ${index + 1}`
    }
  })
})

const addTheSamePosition = () => {
  if (!dataAdd.theSamePositions) {
    dataAdd.theSamePositions = [[null, null]]
    return
  }

  dataAdd.theSamePositions.push([null, null])
}

const deleteTheSamePosition = (index: number) => {
  if (!dataAdd.theSamePositions || dataAdd.theSamePositions.length <= 0) {
    dataAdd.theSamePositions = [[null, null]]
    return
  }

  dataAdd?.theSamePositions?.splice(index, 1)
}

const clearBusinessAndTask = (index: number) => {
  dataAdd.positions[index].tasks = []
  dataAdd.positions[index].businesses = {}
}

defineExpose({
  open
})
</script>
